import React, { useState, useEffect, useCallback, useRef } from "react";

// Función para convertir la llave VAPID (Requerido por los navegadores para Web Push)
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export default function NotificacionesBell({ usuario }) {
  const [notificaciones, setNotificaciones] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const menuRef = useRef(null);
  
  const documento = usuario?.documento || usuario?.cedula;

  // Cierra el modal si el usuario hace clic fuera de él
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setAbierto(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRef]);

  // 1. SUSCRIPCIÓN AL MOTOR PUSH NATIVO
  const suscribirAPush = useCallback(async () => {
    if (!documento || !('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const register = await navigator.serviceWorker.register('/sw.js');
      const publicVapidKey = 'BOP7KCNFsHCtomYr3md4SS6abX8uDvZvHidV4NOCK9lSfrK7alc8BH86a3GPchJ7XrNaEqLflhitStqTas7cgeE'; 

      const subscription = await register.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
      });

      await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/push/subscribe`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documento: documento,
          subscription: subscription
        })
      });

    } catch (error) {
      console.error('Error al registrar la suscripción Push:', error);
    }
  }, [documento]);

  useEffect(() => {
    if (documento) {
      suscribirAPush();
    }
  }, [documento, suscribirAPush]);

  const cargarNotificaciones = useCallback(async () => {
    if (!documento) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/notificaciones/${documento}`);
      if (!res.ok) return; 
      const data = await res.json();
      if (data.success && Array.isArray(data.notificaciones)) {
        setNotificaciones(data.notificaciones);
      }
    } catch (err) {
      console.debug("Aviso temporal de red en la campana de notificaciones:", err.message);
    }
  }, [documento]);

  useEffect(() => {
    cargarNotificaciones();
    const intervalo = setInterval(cargarNotificaciones, 30000);
    return () => clearInterval(intervalo);
  }, [cargarNotificaciones]);

  const marcarComoLeida = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/notificaciones/leer/${id}`, { method: "PUT" });
      setNotificaciones(prev => prev.map(n => n.id === id ? { ...n, leida: 1 } : n));
    } catch (err) {
      console.error("Error al marcar leída:", err);
    }
  };

  const noLeidas = notificaciones.filter(n => !n.leida).length;

  return (
    <div className="relative" ref={menuRef}>
      {/* Botón de la Campana */}
      <button 
        onClick={() => setAbierto(!abierto)}
        className="relative p-2.5 rounded-full bg-white/80 hover:bg-white text-gray-700 shadow-sm border border-gray-200 transition-all cursor-pointer flex items-center justify-center"
      >
        <span className="text-lg">🔔</span>
        {noLeidas > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center shadow-md animate-pulse">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        )}
      </button>

      {/* Menú Desplegable de Notificaciones */}
      {abierto && (
        <div className="absolute right-[-10px] sm:right-0 mt-3 w-[88vw] sm:w-96 max-w-sm bg-white/95 backdrop-blur-2xl rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden animate-fade-in-up origin-top-right">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
            <h3 className="text-xs font-extrabold text-gray-900 uppercase tracking-wider">Centro de Notificaciones</h3>
            <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 font-bold rounded-full">{noLeidas} nuevas</span>
          </div>

          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
            {notificaciones.length > 0 ? (
              notificaciones.map((notif) => (
                <div 
                  key={notif.id} 
                  onClick={() => marcarComoLeida(notif.id)}
                  className={`p-3.5 transition-colors cursor-pointer text-left ${notif.leida ? 'bg-white text-gray-500' : 'bg-blue-50/60 text-gray-900 font-semibold'}`}
                >
                  <p className="text-xs font-bold leading-tight">{notif.titulo}</p>
                  <p className="text-[11px] text-gray-600 mt-1 leading-relaxed break-words">{notif.mensaje}</p>
                  <span className="text-[9px] text-gray-400 mt-1.5 block">{new Date(notif.created_at).toLocaleString()}</span>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-xs text-gray-400">
                No tienes notificaciones recientes.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}